import { Stack, Typography, useTheme } from "@mui/material";
import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useGetPublicCertificate from "src/services/tracking/useGetPublicCertificate";
import FooterLogos from "./components/FooterLogos";
import SimpleTable from "./components/SiteTable";
import moment from "moment-jalaali";
import dolphinCertificate from "./assets/dolphinCertificate.jpg";
import certificateError from "./assets/certificateError.jpg";
import { LoadingScreen } from "src/components/ui";
import { QRCodeCanvas } from "qrcode.react";
import SiteTableMobile from "./components/SiteTableMobile";
import useSecureLogin from "src/services/login/useSecureLogin";
import { UserContext } from "src/contexts/UserContext";

const newTheme = {
  backgroundColor: "#0B2544",
  text: {
    color: {
      primary: "#00FFCB",
    },
  },
};

function getFlagAndUuidFromCombined(combined) {
  const match = combined.match(/flag=([^&]+)&uuid=([^&]+)/);
  if (match) {
    const [, flag, uuid] = match;
    return { flag, uuid };
  }
  return null; // not found
}

function formatDate(date) {
  if (!date) return "";
  return moment(date, "jYYYY-jMM-jDD").format("jYYYY/jMM/jDD");
}

function formatId(input) {
  if (!input) return "";
  const uuid = String(input);
  if (!uuid) return "";
  return `S-${uuid.match(/.{1,4}/g).join("-")}`;
}

function formatNumber(num) {
  const clean = num.replace(/\D/g, "");
  const groups = clean.match(/.{1,4}/g) || [];
  return "U - " + groups.join(" - ");
}

const Tracking = () => {
  const theme = useTheme();
  const { combined } = useParams();
  const trackingLink = process.env.REACT_APP_TRACKING_LINK;
  const invitationRef = useRef(null);
  const [trackingData, setTrackingData] = useState({ flag: "", uuid: "" });
  const [userData, setUserData] = useState({
    title: "",
    nationalId: "",
    qrCode: "",
    siteList: [],
    uuid: "",
  });

  useEffect(() => {
    if (combined) {
      const urlData = getFlagAndUuidFromCombined(combined);
      if (urlData) {
        setTrackingData(urlData);
      }
    }
  }, [combined]);

  const { fetchWSO2Token } = useSecureLogin();
  const { wSO2Token } = useContext(UserContext);
  useEffect(() => {
    if (!wSO2Token) {
      fetchWSO2Token();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wSO2Token]);

  const { data, loading, error } = useGetPublicCertificate(trackingData.uuid, wSO2Token);

  useEffect(() => {
    if (data) {
      const apiData = data.publicCertificate.data;
      setUserData({
        title: apiData.user.name,
        nationalId: extractNationalId(apiData.user),
        qrCode: `${trackingLink}/flag=U&uuid=${apiData.user.uuid}`,
        uuid: apiData.user.uuid,
        siteList: apiData.site_list.map((site, index) => ({
          id: index + 1,
          siteName: site.site_name,
          siteUuid: formatId(site.business_uuid),
          chain: site.requested_chain,
          startDate: formatDate(site.start_date),
          endDate: formatDate(site.end_date),
        })),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  function extractNationalId(data) {
    if (data?.real_legal === "Legal") {
      return `شناسه ملی :  ${data.national_id}`;
    }
    if (data?.real_legal === "Real") {
      return `کد ملی :  ${data.national_id}`;
    }
  }

  function chunkArray(array, size) {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  const chunkedSiteLists = chunkArray(userData.siteList, 6);

  return (
    <Stack
      width="100%"
      height="100%"
      bgcolor={newTheme.backgroundColor}
      sx={{ overflowX: "hidden" }}
      justifyContent={"center"}
      alignItems={"center"}
      flexDirection={"column"}
    >
      {data && (
        <>
          {/* WEB */}
          <Stack
            width={"100%"}
            justifyContent={"center"}
            alignItems={"center"}
            color={theme.palette.common.black}
            display={{ xs: "none", sm: "flex" }}
          >
            {chunkedSiteLists.map((siteChunk, index) => (
              <Stack
                justifyContent="center"
                direction="column"
                ref={invitationRef}
                id="download-box"
                p={2}
                position={"relative"}
                width={1300}
                key={index}
              >
                <img src={dolphinCertificate} alt={"گواهی دلفین"} height={"1794px"} />
                <Stack
                  width={"70%"}
                  height={100}
                  position={"absolute"}
                  top={430}
                  left={195}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Typography variant="h4" textAlign={"center"} fontWeight={900}>
                    گواهی عضویت در زنجیره ارزش آبزیان (دلفین)
                  </Typography>
                </Stack>
                <Stack
                  width={"70%"}
                  height={100}
                  position={"absolute"}
                  top={565}
                  left={195}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Typography variant="h5" textAlign={"center"}>
                    {userData.title}
                  </Typography>
                </Stack>
                <Stack
                  width={"50%"}
                  height={100}
                  position={"absolute"}
                  top={660}
                  left={300}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Typography variant="title">{userData.nationalId}</Typography>
                </Stack>
                <Stack
                  height="200px"
                  width="300px"
                  alignItems="center"
                  flexDirection="column"
                  justifyContent={"center"}
                  position={"absolute"}
                  top={750}
                  left={480}
                >
                  <QRCodeCanvas value={userData.qrCode} size={200} bgColor="transparent" fgColor="#000000" level="H" />
                </Stack>
                <Stack
                  height="200px"
                  width="500px"
                  alignItems="center"
                  flexDirection="column"
                  justifyContent={"center"}
                  position={"absolute"}
                  top={880}
                  left={400}
                >
                  <Typography variant="title"> شناسه عضویت : {formatNumber(userData.uuid)} </Typography>
                </Stack>
                <Stack position={"absolute"} top={1050} right={200}>
                  <SimpleTable dataTable={siteChunk} />
                </Stack>
                <Stack position={"absolute"} top={1650} right={550}>
                  <FooterLogos />
                </Stack>
              </Stack>
            ))}
          </Stack>

          {/* MOBILE */}
          <Stack
            width={"100%"}
            height={"100vh"}
            alignItems={"center"}
            color={theme.palette.common.black}
            display={{ xs: "flex", sm: "none" }}
          >
            {chunkedSiteLists.map((siteChunk, index) => (
              <Stack
                justifyContent="center"
                direction="column"
                ref={invitationRef}
                id="download-box"
                position={"relative"}
                width={400}
                key={index}
              >
                <img src={dolphinCertificate} alt={"گواهی دلفین"} />
                <Stack
                  width={"70%"}
                  height={100}
                  position={"absolute"}
                  top={100}
                  left={60}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Typography variant="buttonLarge" textAlign={"center"} fontWeight={900}>
                    گواهی عضویت در زنجیره ارزش آبزیان (دلفین)
                  </Typography>
                </Stack>
                <Stack
                  width={"70%"}
                  height={30}
                  position={"absolute"}
                  top={175}
                  left={60}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Typography variant="overline" textAlign={"center"}>
                    {userData.title}
                  </Typography>
                </Stack>
                <Stack
                  width={"50%"}
                  height={100}
                  position={"absolute"}
                  top={170}
                  left={100}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Typography variant="caption">{userData.nationalId}</Typography>
                </Stack>
                <Stack
                  height="200px"
                  width="300px"
                  alignItems="center"
                  flexDirection="column"
                  justifyContent={"center"}
                  position={"absolute"}
                  top={160}
                  left={50}
                >
                  <QRCodeCanvas value={userData.qrCode} size={60} bgColor="transparent" fgColor="#000000" level="H" />
                </Stack>
                <Stack
                  height="200px"
                  width="500px"
                  alignItems="center"
                  flexDirection="column"
                  justifyContent={"center"}
                  position={"absolute"}
                  top={210}
                  left={-50}
                >
                  <Typography variant="tiny"> شناسه عضویت : {formatNumber(userData.uuid)} </Typography>
                </Stack>
                <Stack position={"absolute"} top={320} left={40}>
                  <SiteTableMobile dataTable={siteChunk} />
                </Stack>
                <Stack position={"absolute"} top={510} left={150}>
                  <FooterLogos isMobile />
                </Stack>
              </Stack>
            ))}
          </Stack>
        </>
      )}
      {!data && error && (
        <Stack
          width={"100%"}
          justifyContent={"center"}
          alignItems={"center"}
          color={theme.palette.common.black}
          flexDirection={"column"}
          height={"100vh"}
        >
          <Stack
            justifyContent="center"
            alignItems={"center"}
            direction="column"
            ref={invitationRef}
            id="download-box"
            p={2}
            bgcolor={newTheme.backgroundColor}
            height={"100%"}
            maxWidth={1000}
            position={"relative"}
          >
            <img src={certificateError} alt={"گواهی دلفین"} />
            <Stack
              width={"50%"}
              height={100}
              position={"absolute"}
              top="50%"
              justifyContent={"center"}
              alignItems={"center"}
            >
              <Typography
                color={"error.main"}
                textAlign={"center"}
                sx={{
                  fontSize: {
                    xs: 20,
                    sm: 32,
                  },
                  fontWeight: {
                    xs: 600,
                    sm: 700,
                  },
                }}
              >
                {error?.message}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      )}
      <LoadingScreen open={loading} title={"لطفا منتظر بمانید..."} />
    </Stack>
  );
};
export default Tracking;
